import React from 'react';
import { AlertCircle, X } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import type { ValidationError } from '@/hooks/useSettingsValidation';

interface ValidationErrorSummaryProps {
  errors: ValidationError[];
  onDismiss: () => void;
  onNavigateToError?: (tabId: string, fieldId: string) => void;
}

export const ValidationErrorSummary: React.FC<ValidationErrorSummaryProps> = ({
  errors,
  onDismiss,
  onNavigateToError
}) => {
  if (errors.length === 0) return null;
  
  const errorsByTab = errors.reduce((acc, error) => {
    if (!acc[error.tab]) {
      acc[error.tab] = [];
    }
    acc[error.tab].push(error);
    return acc;
  }, {} as Record<string, ValidationError[]>);
  
  const getTabLabel = (tabId: string) => {
    const labels: Record<string, string> = {
      'working-hours': 'Working Hours & Policies',
      'submissions-deadlines': 'Submissions & Deadlines',
      'time-entry': 'Time Entry Rules',
      'utilisation': 'Utilisation'
    };
    return labels[tabId] || tabId;
  };
  
  return (
    <Alert variant="destructive" className="relative" showIcon={false}>
      <AlertTitle className="flex items-center justify-between">
        <span className="flex items-center gap-2">
          <AlertCircle className="h-4 w-4" />
          Please fix {errors.length} validation error{errors.length > 1 ? 's' : ''}
        </span>
        <Button
          variant="ghost"
          size="sm"
          className="h-6 w-6 p-0 hover:bg-destructive/20"
          onClick={onDismiss}
        >
          <X className="h-4 w-4" />
        </Button>
      </AlertTitle>
      <AlertDescription>
        <div className="mt-2 space-y-3">
          {Object.entries(errorsByTab).map(([tabId, tabErrors]) => (
            <div key={tabId}>
              <div className="font-medium text-sm mb-1">{getTabLabel(tabId)}</div>
              <ul className="space-y-1 ml-4">
                {tabErrors.map((error, index) => (
                  <li key={index} className="text-sm flex items-start gap-2">
                    <span className="text-destructive">•</span>
                    <span className="flex-1">
                      {error.message}
                      {onNavigateToError && (
                        <button
                          onClick={() => onNavigateToError(error.tab, error.field)}
                          className="ml-2 underline hover:no-underline"
                        >
                          Go to field
                        </button>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </AlertDescription>
    </Alert>
  );
};
